import {
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
} from 'material-react-table';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  buildRememberTableParams,
  parseRememberTableParams,
} from './useRememberTableParams';

interface Props {
  initialColumnFilters?: MRT_ColumnFiltersState;
  initialGlobalFilter?: string;
  initialPageSize?: number;
  initialSorting?: MRT_SortingState;
  paramPrefix?: string;
}

interface RememberTableState {
  columnFilters: MRT_ColumnFiltersState;
  globalFilter?: string;
  pagination: MRT_PaginationState;
  sorting: MRT_SortingState;
}

const serializeRememberTableState = (state: RememberTableState) =>
  JSON.stringify({
    columnFilters: state.columnFilters,
    globalFilter: state.globalFilter ?? '',
    pagination: state.pagination,
    sorting: state.sorting,
  });

const rememberTableStatesEqual = (
  left: RememberTableState,
  right: RememberTableState
) => serializeRememberTableState(left) === serializeRememberTableState(right);

export default function useRememberTable({
  initialColumnFilters,
  initialGlobalFilter,
  initialPageSize,
  initialSorting,
  paramPrefix,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialMount = useRef(true);
  const latestSearchParamsRef = useRef(searchParams);
  const previousSearchParamsStringRef = useRef(searchParams.toString());
  const skipNextUrlSyncRef = useRef(false);
  const defaultPageSize = initialPageSize ?? 20;
  const searchParamsString = searchParams.toString();
  const tableParams = parseRememberTableParams(
    new URLSearchParams(searchParamsString),
    {
      initialColumnFilters,
      initialGlobalFilter,
      initialPageSize,
      initialSorting,
      paramPrefix,
    }
  );
  const tableParamsKey = serializeRememberTableState(tableParams);
  const tableParamsRef = useRef<RememberTableState>(tableParams);
  tableParamsRef.current = tableParams;

  const [pagination, setPagination] = useState<MRT_PaginationState>(
    tableParams.pagination
  );

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    tableParams.columnFilters
  );
  const [sorting, setSorting] = useState<MRT_SortingState>(tableParams.sorting);
  const [globalFilter, setGlobalFilter] = useState(tableParams.globalFilter);
  const currentTableStateRef = useRef<RememberTableState>(tableParams);
  currentTableStateRef.current = {
    columnFilters,
    globalFilter,
    pagination,
    sorting,
  };

  useEffect(() => {
    latestSearchParamsRef.current = searchParams;
  }, [searchParams, searchParamsString]);

  useEffect(() => {
    const nextTableState = tableParamsRef.current;
    const tableStateChanged = !rememberTableStatesEqual(
      currentTableStateRef.current,
      nextTableState
    );

    if (previousSearchParamsStringRef.current !== searchParamsString) {
      previousSearchParamsStringRef.current = searchParamsString;
      skipNextUrlSyncRef.current = tableStateChanged;
    }

    if (!tableStateChanged) {
      return;
    }

    setColumnFilters(nextTableState.columnFilters);
    setPagination(nextTableState.pagination);
    setSorting(nextTableState.sorting);
    setGlobalFilter(nextTableState.globalFilter);
  }, [searchParamsString, tableParamsKey]);

  // Consolidated effect to update URL params without creating history entries
  useEffect(() => {
    // Skip updating URL on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (skipNextUrlSyncRef.current) {
      skipNextUrlSyncRef.current = false;
      return;
    }

    const newParams = buildRememberTableParams(latestSearchParamsRef.current, {
      columnFilters,
      defaultPageSize,
      globalFilter,
      initialSorting,
      pagination,
      sorting,
      paramPrefix,
    });

    // Only update if params have actually changed
    const currentParams = latestSearchParamsRef.current.toString();
    const newParamsString = newParams.toString();

    if (currentParams !== newParamsString) {
      // Use replace: true to avoid creating new history entries
      setSearchParams(newParams, { replace: true });
    }
  }, [
    columnFilters,
    defaultPageSize,
    globalFilter,
    initialSorting,
    pagination,
    paramPrefix,
    setSearchParams,
    sorting,
  ]);

  return {
    pagination,
    setPagination,
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
    globalFilter,
    setGlobalFilter,
  };
}
